export type Vec2 = { x: number; z: number };

export type Viewport = {
  /** World coordinates at the centre of the canvas. */
  cx: number;
  cz: number;
  /** Pixels per metre. */
  scale: number;
  width: number;
  height: number;
};

export function worldToScreen(v: Viewport, x: number, z: number): { sx: number; sy: number } {
  return {
    sx: (x - v.cx) * v.scale + v.width / 2,
    sy: v.height / 2 - (z - v.cz) * v.scale,
  };
}

export function screenToWorld(v: Viewport, sx: number, sy: number): Vec2 {
  return {
    x: (sx - v.width / 2) / v.scale + v.cx,
    z: (v.height / 2 - sy) / v.scale + v.cz,
  };
}

export function distance(ax: number, az: number, bx: number, bz: number): number {
  return Math.hypot(ax - bx, az - bz);
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Chooses a grid step that keeps grid lines roughly 80px apart on screen. */
export function gridStep(scale: number): number {
  const targetPx = 80;
  const candidates = [10, 25, 50, 100, 250, 500, 1000, 2500, 5000];
  for (const step of candidates) {
    if (step * scale >= targetPx) return step;
  }
  return candidates[candidates.length - 1];
}

/** Formats a metre value for the HUD without noisy decimals. */
export function fmtMeters(value: number): string {
  return Math.abs(value) >= 100 ? value.toFixed(0) : value.toFixed(1);
}

/** Evenly-ish distributed random point inside a circle. */
export function randomPointInCircle(cx: number, cz: number, radius: number, rng: () => number = Math.random): Vec2 {
  const angle = rng() * Math.PI * 2;
  const r = Math.sqrt(rng()) * radius;
  return { x: cx + Math.cos(angle) * r, z: cz + Math.sin(angle) * r };
}

export type Rect = { x0: number; z0: number; x1: number; z1: number };

export function normalizeRect(a: Vec2, b: Vec2): Rect {
  return {
    x0: Math.min(a.x, b.x),
    z0: Math.min(a.z, b.z),
    x1: Math.max(a.x, b.x),
    z1: Math.max(a.z, b.z),
  };
}

export function rectContains(rect: Rect, x: number, z: number): boolean {
  return x >= rect.x0 && x <= rect.x1 && z >= rect.z0 && z <= rect.z1;
}
